import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ValidationErrorModal as ValidationErrorModalType } from '@/schemas/sceneDataSchema';
import { useSceneStore } from '@/stores/scene-store';
import useModal from '@/hooks/use-modals';
import { ModalDialog } from './ModalDialog';
import { Typography } from '@mui/material';
import useAnalytics from '@/hooks/use-analytics';
import { DynamicRenderer } from './DynamicRenderer';
import { RouteFn } from '@/hooks/use-flow';
import { GenericButton } from './formElements/buttons/GenericButton';
import { ButtonColors, ButtonSizes, ButtonVariants } from '@/constants/buttonTypes';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { persistStep } from '@/api/agency-quote-api';
import { applyPersistData } from '@/stores/sync';
import { getRouteStep } from '@/services/location-service';
import { reportError } from '@/services/error-reporting-service';
import { useFormContext } from 'react-hook-form';

interface ValidationErrorModalProps {
  component: ValidationErrorModalType;
  route: RouteFn;
}

function resolveEntityDisplay(
  template: string,
  scope: string,
  entityIndex: number,
  serverState: ReturnType<typeof useSceneStore.getState>['serverState']
): string {
  return template.replace(/\{(\w+)\}/g, (_match, attr) => {
    const entry = Object.values(serverState).find(
      (f) => f.scope === scope && f.attribute === attr && f.entity_index === entityIndex
    );
    return entry?.value != null ? String(entry.value) : '';
  });
}

export function ValidationErrorModal({ component, route }: ValidationErrorModalProps) {
  const { getModal } = useModal();
  const { trackEvent } = useAnalytics();
  const form = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const prevMatchedMessage = useRef<string | null>(null);
  // Stale-ref so the effect always calls the latest getModal without being in its dep array.
  const getModalRef = useRef(getModal);
  useEffect(() => {
    getModalRef.current = getModal;
  });

  const matchedMessage = useSceneStore((state) => {
    for (const field of Object.values(state.serverState)) {
      if (
        field.scope === component.scope &&
        field.attribute === component.attribute &&
        (component.entity_index === null || field.entity_index === component.entity_index) &&
        field.valid === false &&
        field.message != null &&
        new RegExp(component.error_pattern).test(field.message)
      ) {
        return field.message;
      }
    }
    return null;
  });

  const errorEntities = useMemo(() => {
    if (!matchedMessage) return [];
    const { serverState } = useSceneStore.getState();
    const indices: number[] = [];
    for (const field of Object.values(serverState)) {
      if (
        field.scope === component.scope &&
        field.attribute === component.attribute &&
        field.valid === false &&
        field.message != null &&
        new RegExp(component.error_pattern).test(field.message)
      ) {
        indices.push(field.entity_index);
      }
    }
    return indices.map((idx) => ({
      index: idx,
      label: component.entity_display
        ? resolveEntityDisplay(component.entity_display, component.scope, idx, serverState)
        : null,
    }));
  }, [matchedMessage, component]);

  useEffect(() => {
    const isNewError = matchedMessage !== null && prevMatchedMessage.current === null;
    prevMatchedMessage.current = matchedMessage;

    if (!isNewError) return;

    trackEvent(`${component.id}_AUTO_OPENED`);

    // Defer one frame so ModalDialog's registration effect runs first on initial mount.
    const rafId = requestAnimationFrame(() => {
      getModalRef.current(component.id)?.open();
    });

    return () => cancelAnimationFrame(rafId);
  }, [matchedMessage, component.id, trackEvent]);

  const handleDismiss = useCallback(async () => {
    getModal(component.id)?.close();

    const { step_id } = getRouteStep();
    if (!step_id) return;

    const entityIndices = errorEntities.map((e) => e.index);
    if (entityIndices.length === 0) return;

    setIsLoading(true);
    try {
      for (const entityIndex of entityIndices) {
        const key = encodeFieldKey({
          scope: component.scope,
          attribute: component.attribute,
          entity_index: entityIndex,
        });
        useSceneStore.getState().setValue(key, null);
        form.setValue(key, null);
        const sceneData = await persistStep(step_id, entityIndex, { [key]: null });
        applyPersistData(sceneData.step_state, [key]);
        const updated = useSceneStore.getState().serverState[key];
        if (updated?.valid === false && updated.message) {
          form.setError(key, { type: 'server', message: updated.message });
        } else {
          form.clearErrors(key);
        }
      }
    } catch (error) {
      reportError({ error });
    } finally {
      setIsLoading(false);
    }
  }, [component.id, component.scope, component.attribute, getModal, errorEntities, form]);

  const dismissLabel = component.dismiss_label ?? 'OK';

  return (
    <ModalDialog
      id={component.id}
      status='closed'
      sticky_footer={false}
      footerChildren={
        <div style={{ width: '100%' }}>
          {component.modal_footer && (
            <DynamicRenderer
              components={component.modal_footer}
              route={route}
            />
          )}
          <GenericButton
            id={`${component.id}_dismiss`}
            label={dismissLabel}
            onClick={() => void handleDismiss()}
            color={ButtonColors.PRIMARY}
            size={ButtonSizes.LARGE}
            variant={ButtonVariants.CONTAINED}
            fullWidth
            loading={isLoading}
          />
        </div>
      }
    >
      <Typography variant='h2'>{component.modal_title}</Typography>
      <DynamicRenderer
        components={component.modal_body}
        route={route}
      />
      {errorEntities.length > 0 && errorEntities.some((e) => e.label) && (
        <ul style={{ margin: 0 }}>
          {errorEntities.map((e) => (
            <li key={e.index}>
              <Typography variant='body1'>{e.label}</Typography>
            </li>
          ))}
        </ul>
      )}
    </ModalDialog>
  );
}
